import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MarketingNav, Footer } from "@/components/Marketing";
import { api, mediaUrl } from "@/lib/api";
import { themeOf } from "@/lib/themes";
import { Eye } from "lucide-react";

const CATS = ["Featured", "History", "Science", "Technology", "Culture", "Business"];

export default function Discover() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("Featured");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/discover", { params: cat === "Featured" ? {} : { category: cat } });
      setItems(data);
    })();
  }, [cat]);

  return (
    <div className="bg-background min-h-screen">
      <MarketingNav />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Discover</div>
        <h1 className="font-display text-5xl font-semibold tracking-tight text-foreground">Timelines worth exploring</h1>
        <p className="mt-3 text-muted-foreground max-w-lg">A curated, editorial collection of beautiful public timelines.</p>

        <div className="mt-10 flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`discover-cat-${c}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${cat === c ? "bg-primary text-primary-foreground" : "border border-border text-foreground/70 hover:text-foreground"}`}>{c}</button>
          ))}
        </div>

        {items.length === 0 ? (
          <p className="mt-16 text-muted-foreground">No timelines in this category yet.</p>
        ) : (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((tl, i) => {
              const t = themeOf(tl.theme);
              return (
                <motion.div key={tl.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Link to={`/t/${tl.creator_username}/${tl.slug}`} data-testid={`discover-card-${tl.slug}`}
                    className="group block rounded-2xl border border-border bg-card overflow-hidden transition-transform duration-300 hover:-translate-y-1">
                    <div className="h-44 relative overflow-hidden" style={{ background: t.bgGradient }}>
                      {tl.cover_image && <img src={mediaUrl(tl.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-85 transition-transform duration-500 group-hover:scale-105" loading="lazy" />}
                      <span className="absolute top-3 left-3 text-[11px] px-2 py-0.5 rounded-full glass font-medium" style={{ color: t.text }}>{tl.category}</span>
                    </div>
                    <div className="p-6">
                      <h3 className="font-display text-lg font-semibold text-foreground group-hover:text-primary transition-colors">{tl.title}</h3>
                      <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{tl.description}</p>
                      <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                        <span>by @{tl.creator_username}</span>
                        <span className="flex items-center gap-1"><Eye size={13} /> {tl.views?.toLocaleString()} · {tl.event_count} events</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
